import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react"
import { useConfig } from "@/config/context"
import { EventStore } from "@/nostr/EventStore"
import { RelayPool } from "@/nostr/RelayPool"
import type { RelayDescriptor, RelayStatus } from "@/nostr/types"
import type { Signer } from "@/nostr/signers"
import { nowSeconds } from "@/utils/time"
import { useToast } from "./ToastContext"

interface NostrContextValue {
  pool: RelayPool
  store: EventStore
  statuses: RelayStatus[]
  connectedCount: number
  setSigner: (signer: Signer | undefined) => void
}

const NostrContext = createContext<NostrContextValue | undefined>(undefined)

function descriptorsFromUrls(urls: string[]): RelayDescriptor[] {
  return urls.map((url) => ({ url, read: true, write: true, priority: 100 }))
}

/**
 * Creates the long lived protocol objects: the event store (shared cache) and
 * the relay pool (one WebSocket per relay). Everything else in the app reads
 * relay data through these two instances.
 */
export function NostrProvider({ children }: { children: ReactNode }) {
  const config = useConfig()
  const { push } = useToast()
  const signerRef = useRef<Signer | undefined>(undefined)
  const recentNotices = useRef(new Map<string, number>())

  const [store] = useState(() => new EventStore())
  const [pool] = useState(
    () =>
      new RelayPool({
        relayOptions: config.relays.relayOptions,
        relays: descriptorsFromUrls(config.relays.defaultRelays),
        onNotice: (message) => {
          const normalized = message.toLowerCase()
          // Relay-level throttling and transport noise is shown in the relay
          // status/banner, never as one toast per relay.
          if (/too many|concurrent|rate.?limit|pingpong|timed ?out|websocket error/.test(normalized)) {
            return
          }
          const now = Date.now()
          const last = recentNotices.current.get(message)
          if (last && now - last < 60_000) return
          recentNotices.current.set(message, now)
          if (recentNotices.current.size > 50) recentNotices.current.clear()
          push("error", message)
        },
        authenticate: async (challenge, relayUrl) => {
          const signer = signerRef.current
          if (!signer) return undefined
          return signer.signEvent({
            kind: 22242,
            content: "",
            tags: [
              ["relay", relayUrl],
              ["challenge", challenge],
            ],
            created_at: nowSeconds(),
          })
        },
      }),
  )

  useEffect(() => {
    // Close every socket when the provider is torn down (page unload / HMR).
    return () => pool.closeAll()
  }, [pool])

  const statuses = useSyncExternalStore(pool.subscribeToStatus, pool.getStatusSnapshot, pool.getStatusSnapshot)

  const value = useMemo<NostrContextValue>(
    () => ({
      pool,
      store,
      statuses,
      connectedCount: statuses.filter((status) => status.state === "connected").length,
      setSigner: (signer) => {
        signerRef.current = signer
      },
    }),
    [pool, store, statuses],
  )

  return <NostrContext.Provider value={value}>{children}</NostrContext.Provider>
}

export function useNostr(): NostrContextValue {
  const context = useContext(NostrContext)
  if (!context) throw new Error("useNostr must be used inside a NostrProvider")
  return context
}

/** Re-renders whenever the shared event store changes. */
export function useEventVersion(store: EventStore): number {
  return useSyncExternalStore(
    store.subscribe,
    () => store.getVersion(),
    () => store.getVersion(),
  )
}

